import { mapCustomerAttributeTypeToSaveType } from '@common/customerAttributes/mapCustomerAttributeTypeToSaveType.js';
import {
  CustomerAttribute, CustomerAttributeSave,
} from '@common/customerAttributes/types.js';
import type { MfeLogPayload } from '@common/utils/getMfeLogPayload.js';
import { ClientAbstract } from '@src/classes/ClientAbstract.js';
import {
  ApiLoadConsentResponse,
  ConsentSaveType,
} from '@src/types/common.js';

type ConsentAndCustomerAttributeSaveType = ConsentSaveType | CustomerAttributeSave;

export class ClientComs extends ClientAbstract {
  async getConsents(lifecycle: string) {
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();

    const path = lcId
      ? `/consent/sp/${spId}/customer/${lcId}`
      : `/consent/sp/${spId}/register`;

    const qs = {
      lifecycle,
      textformat: 'html',
      ...(lcId ? { ignoreNotFound: true } : {}),
      additionalInfo: true,
      pageformat: true,
      includeCustomerAttributes: true,
    };

    return this.httpClient.get<ApiLoadConsentResponse>(path, qs);
  }

  async saveConsents(consentData: ConsentSaveType[], customerAttributesData: CustomerAttribute[] = []) {
    let channelMerge : string | undefined;
    let consentsAndCustomerAttributes : ConsentAndCustomerAttributeSaveType[] = [];

    const { lcId, spId, registerId } = this.getCustomerInfoFromConfigOrAuth();

    const {
      username = 'unknown',
    } = this.auth;

    const {
      references,
      comment,
      usertype,
      lifecycle,
      newcustomer,
      channel = 'Web',
      vonr = '',
      mode = '',
    } = this.config;

    const qs: Record<string, string | boolean> = {
      lifecycle,
    };

    if (newcustomer) {
      qs.isNewCustomer = true;
    }

    if (mode === 'merge') {
      qs.isMerge = true;
      channelMerge = 'Merge';
    }

    const headers: Record<string, string> = {
      'tef-coms-channel': channelMerge ?? channel,
      'tef-coms-username': vonr ? `${vonr} | ${username}` : username,
    };

    if (references) {
      headers['tef-coms-references'] = references;
    }
    if (comment) {
      headers['tef-coms-comment'] = comment;
    }
    if (usertype) {
      headers['tef-coms-usertype'] = usertype;
    }

    // Collect consents and customer attributes
    const consentsSaveData = consentData.map((consent) => ({ ...consent, type: 'consent' }));
    const customerAttributesSaveData = customerAttributesData.map(
      (customerAttributeData) => mapCustomerAttributeTypeToSaveType(customerAttributeData),
    );
    consentsAndCustomerAttributes = [...consentsSaveData, ...customerAttributesSaveData];

    const path = lcId
      ? `/consent/sp/${spId}/customer/${lcId}`
      : `/consent/sp/${spId}/register/${registerId}`;

    return this.httpClient.post(path, consentsAndCustomerAttributes, qs, headers);
  }

  logMicrofrontendParams(mfeLogEntryPayload: MfeLogPayload): Promise<void> {
    return this.httpClient.post('/service/mfe/log', mfeLogEntryPayload);
  }
}
